import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../../utils/api";
import { Validation } from "../../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../../@types/snackbarAPI.types";
import JSONToExcel from "../../../../../utils/commonFunctions/JSONToExcel";
import { useEmployerRoute } from "../../../../../hooks";
import { Group } from "../../../../../@types/group.types";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { InvalidStateForMemberReportResponseType } from "../../../../../@types/reportType.types";

const chipMessage = "This report will pull all the invalid or null state for the members.";

const InvalidStateForMemberReport = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<any>({
		alternative_report_name: "",
		group_number: null
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		// employee validation checking
		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Invalid State For Member Report Generation");
					const { group_number } = reportFieldDetails;

					const invalidStateForMemberPayload = {
						group_number
					};

					// console.log("invalidStateForMemberPayload", invalidStateForMemberPayload);
					const response: InvalidStateForMemberReportResponseType[] = await trackPromise(
						api.groupReport.invalidStateForMemberReport(invalidStateForMemberPayload)
					);

					if (response) {
						// console.log("Invalid State For Member Response", response);

						const fileName = reportFieldDetails.alternative_report_name
							? reportFieldDetails.alternative_report_name
							: "Invalid State For Member Report";

						const excelPayload = {
							user: {
								role: user?.role,
								name: user?.first_name + " " + user?.last_name
							},
							reportFieldDetails,
							response
						};

						await trackPromise(JSONToExcel(excelPayload, fileName));

						setTimeout(() => {
							setSnackbarAPICallProps(
								Object.assign({}, snackbarAPICallProps, {
									open: true,
									message:
										"Invalid State For Member Report Fetched Successfully and File Downloaded!",
									severity: "success"
								})
							);
						}, 2000);
					}
				}
			} catch (error) {
				alert("Invalid State For Member Report Generation Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails, snackbarAPICallProps, user]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.invalid_state_for_member_report);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Invalid State For Member Report Generation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Alternative Report Name",
							name: "alternative_report_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Enter alternative report name",
							value: reportFieldDetails.alternative_report_name,
							type: "textfield"
						}
					]
				}
			)
		);
	}, [assignedGroups, reportFieldDetails, handleChange, isEmployerRoute, queryGroupNumber, selectedGroup?.name]);

	// console.log("reportFieldDetails", reportFieldDetails);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				handleClearDateField={() => null}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default InvalidStateForMemberReport;
